'use client';

import { AibackReportAspectRowEntity } from '@/entities/aiback-report.entity';
import { ReportModalAspectRadarPoint } from '@/domain/reporting/reporting-modal-aggregations';
import {
    formatInteger,
    formatPercent,
    formatSignedDelta,
    formatRating as formatRatingValue,
} from '@/domain/reporting/reporting-shared.formatters';
import Rating from '@/ui/Rating';
import { FC } from 'react';
import ReportModalAspectRadarChart from '@/components/products-report-modal/ReportModalAspectRadarChart';
import Image from 'next/image';
import { activeStar, dimStar } from '@/assets';

type ReportModalAspectsSectionProps = {
    aspectRows: AibackReportAspectRowEntity[];
    aspectRadarPoints: ReportModalAspectRadarPoint[];
    positiveHighlights: string[];
    positiveHighlightsHiddenCount?: number;
    negativeHighlights: string[];
    negativeHighlightsHiddenCount?: number;
    neutralHighlights?: string[];
    neutralHighlightsHiddenCount?: number;
};

const clamp = (value: number, min: number, max: number): number => {
    return Math.min(max, Math.max(min, value));
};

const ONE_STAR_SIZE = 11;

const ResponsiveRating = ({ value }: { value: number | null }) => {
    if (value === null) {
        return <span>{formatRatingValue(value)}</span>;
    }

    const oneStarFillPercent = clamp(value / 5, 0, 1) * 100;

    return (
        <>
            <div className="hidden min-[700px]:block">
                <Rating value={value} size={11} />
            </div>
            <span
                className="relative inline-block shrink-0 min-[700px]:hidden"
                style={{ width: ONE_STAR_SIZE, height: ONE_STAR_SIZE }}
                aria-hidden="true"
            >
                <Image src={dimStar} alt="" width={ONE_STAR_SIZE} height={ONE_STAR_SIZE} />
                {oneStarFillPercent > 0 ? (
                    <span className="absolute inset-0 overflow-hidden" style={{ width: `${oneStarFillPercent}%` }}>
                        <Image
                            src={activeStar}
                            alt=""
                            width={ONE_STAR_SIZE}
                            height={ONE_STAR_SIZE}
                            className="max-w-none"
                        />
                    </span>
                ) : null}
            </span>
            <span>{formatRatingValue(value)}</span>
        </>
    );
};

const SentimentHighlightsCard = ({
    title,
    items,
    hiddenCount = 0,
    tone,
}: {
    title: string;
    items: string[];
    hiddenCount?: number;
    tone: 'positive' | 'negative' | 'neutral';
}) => {
    const accentClasses =
        tone === 'positive'
            ? 'border-emerald-200 bg-emerald-50/70 text-emerald-700'
            : tone === 'negative'
              ? 'border-rose-200 bg-rose-50/70 text-rose-700'
              : 'border-slate-200 bg-slate-100/80 text-slate-700';

    return (
        <div className={`rounded-lg border p-4 ${accentClasses}`}>
            <h4 className="text-[15px] font-semibold">{title}</h4>
            <ol className="mt-3 space-y-2 pl-5 text-sm leading-6 marker:font-semibold">
                {items.map((item, index) => (
                    <li key={`${tone}-${index}-${item}`} className="text-slate-700">
                        {item}
                    </li>
                ))}
            </ol>
            {hiddenCount > 0 ? (
                <p className="mt-3 text-xs font-medium text-slate-500">
                    {`Показаны первые ${formatInteger(items.length)}. Еще ${formatInteger(hiddenCount)} пунктов не показано.`}
                </p>
            ) : null}
        </div>
    );
};

const ReportModalAspectsSection: FC<ReportModalAspectsSectionProps> = ({
    aspectRows,
    aspectRadarPoints,
    positiveHighlights,
    positiveHighlightsHiddenCount = 0,
    negativeHighlights,
    negativeHighlightsHiddenCount = 0,
    neutralHighlights = [],
    neutralHighlightsHiddenCount = 0,
}) => {
    const visibleHighlightCards = [
        positiveHighlights.length ? (
            <SentimentHighlightsCard
                key="positive-highlights"
                title="Положительное"
                items={positiveHighlights}
                hiddenCount={positiveHighlightsHiddenCount}
                tone="positive"
            />
        ) : null,
        negativeHighlights.length ? (
            <SentimentHighlightsCard
                key="negative-highlights"
                title="Отрицательное"
                items={negativeHighlights}
                hiddenCount={negativeHighlightsHiddenCount}
                tone="negative"
            />
        ) : null,
        neutralHighlights.length ? (
            <SentimentHighlightsCard
                key="neutral-highlights"
                title="Нейтральное"
                items={neutralHighlights}
                hiddenCount={neutralHighlightsHiddenCount}
                tone="neutral"
            />
        ) : null,
    ].filter(Boolean);

    return (
        <section data-testid="report-modal-page-2" className="space-y-4">
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
                <div className="space-y-3 xl:col-span-4">
                    <h3 className="text-sm font-semibold text-slate-900">Сводный анализ по группам аспектов</h3>
                    <ReportModalAspectRadarChart points={aspectRadarPoints} />
                </div>
                <div className="xl:col-span-8">
                    <table className="w-full table-fixed text-xs text-slate-700">
                        <thead>
                            <tr className="border-y border-slate-200 bg-slate-50">
                                <th className="w-[28%] px-2 py-1.5 text-left font-medium">Аспект</th>
                                <th className="w-[10%] px-2 py-1.5 text-right font-medium">Отзывов</th>
                                <th className="w-[10%] px-2 py-1.5 text-right font-medium">Изучено</th>
                                <th className="w-[8%] px-2 py-1.5 text-right font-medium">%</th>
                                <th className="w-[17%] px-2 py-1.5 text-left font-medium">★ из 5 было</th>
                                <th className="w-[17%] px-2 py-1.5 text-left font-medium">★ из 5 стало</th>
                                <th className="w-[10%] px-2 py-1.5 text-right font-medium">Изм.</th>
                            </tr>
                        </thead>
                        <tbody>
                            {aspectRows.map((row) => (
                                <tr key={row.key} className="border-b border-slate-100 align-middle">
                                    <td className="px-2 py-1.5">{row.title}</td>
                                    <td className="px-2 py-1.5 text-right">{formatInteger(row.reviews)}</td>
                                    <td className="px-2 py-1.5 text-right">{formatInteger(row.analyses)}</td>
                                    <td className="px-2 py-1.5 text-right">{formatPercent(row.analysesShare, 1)}</td>
                                    <td className="px-2 py-1.5">
                                        <div className="flex items-center gap-2">
                                            <ResponsiveRating value={row.ratingWas} />
                                        </div>
                                    </td>
                                    <td className="px-2 py-1.5">
                                        <div className="flex items-center gap-2">
                                            <ResponsiveRating value={row.ratingNow} />
                                        </div>
                                    </td>
                                    <td className="px-2 py-1.5 text-right">{formatSignedDelta(row.delta)}</td>
                                </tr>
                            ))}
                            {!aspectRows.length && (
                                <tr>
                                    <td className="px-2 py-3 text-center text-slate-500" colSpan={7}>
                                        Нет аспектов с анализом в выбранном периоде.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            {visibleHighlightCards.length ? (
                <div
                    className={`grid gap-4 ${
                        visibleHighlightCards.length >= 3
                            ? 'grid-cols-1 xl:grid-cols-3'
                            : visibleHighlightCards.length > 1
                              ? 'grid-cols-1 xl:grid-cols-2'
                              : 'grid-cols-1'
                    }`}
                >
                    {visibleHighlightCards}
                </div>
            ) : null}
        </section>
    );
};

export default ReportModalAspectsSection;
